import { useState, useEffect, useRef } from "react";

/**
 * Quotes shown during the website build loading screen.
 */
const buildQuotes = [
  "Building a website used to take days... now it takes 3 minutes and you don't even need pants.",
  "This should take about 3 minutes... maybe ponder the meaning of life?",
  "Rome wasn't built in a day. Your landing page will be ready in about 3 minutes.",
  "Fun fact: in the time it takes to build your page, you could solve a Rubik's cube 3 times, if were like... really good at it.",
  "A team of highly caffeinated AI agents is working on your page right now.",
  "Your landing page is being assembled with love, math, and a third, presumably funny thing.",
  "In 2005 this would've cost $10,000 and taken 6 weeks. Yet, I'm still somehow frustrated it's taking so long.",
  "While you wait: did you know octopuses have three hearts? Now you do.",
  "Generating pixels... aligning divs... now you never have to listen to a podcast about CSS.",
  "Some people juggle while they wait. We recommend just sitting there.",
  "Every masterpiece takes time. Except Jackson Pollocks. I think those were pretty quick.",
  "Behind the scenes: AI agents are having a very productive meeting about your brand voice.",
  "Good things come to those who wait. But that's what they say when it's taking too long.",
  "Making sure every pixel is in the right place... ah, shoot... that Radiohead song is stuck in my head now.",
];

/**
 * Quotes shown during the deploy loading screen.
 * "We meet again..." is always first (user has already seen one loading screen).
 */
const deployQuotes = [
  "We meet again...",
  "Almost there. Your website is about to meet the internet.",
  "Deploying at the speed of... well, a really fast deployment.",
  "Your landing page is putting on its best outfit for the world.",
  "Somewhere, a server is very excited to host your page.",
  "We're rolling out the red carpet for your website right now.",
  "This is the part where your idea becomes real. No pressure.",
  "Launching a website is basically a rocket launch, minus the countdown.",
  "DNS propagation is basically magic. Dark, confusing magic.",
  "Your website is stretching, warming up its <body> tags.",
  "We're connecting approximately one million wires. Approximately.",
  "The internet is about to get slightly more interesting.",
  "Buckle up. Your page is about to go live.",
];

export type QuotePool = "build" | "deploy";

const quotePools: Record<QuotePool, string[]> = {
  build: buildQuotes,
  deploy: deployQuotes,
};

function shuffle<T>(arr: T[]): T[] {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Returns a rotating quote from the given pool, changing every `intervalMs`.
 * Shuffles on mount so repeat visits feel fresh.
 * For "deploy" pool, "We meet again..." is always first.
 */
function buildOrder(pool: QuotePool, quotes: string[]): string[] {
  if (pool === "deploy") {
    const [first, ...rest] = quotes;
    return [first, ...shuffle(rest)];
  }
  return shuffle(quotes);
}

export function useRotatingQuote(pool: QuotePool, intervalMs = 5000) {
  const quotes = quotePools[pool];
  const orderedQuotes = useRef(buildOrder(pool, quotes));

  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % orderedQuotes.current.length);
        setVisible(true);
      }, 300);
    }, intervalMs);

    return () => clearInterval(timer);
  }, [intervalMs]);

  return { quote: orderedQuotes.current[index], visible };
}
